import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Tag,
  Space,
  Typography,
  Alert,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CreditPlan {
  id: string;
  plan_code: string;
  plan_name: string;
  default_credits: number;
  price: number;
  currency: string;
  description: string;
  is_active: boolean;
  is_custom: boolean;
  created_at: string;
}

const CreditPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<CreditPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState<CreditPlan | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/super-admin/plans');
      setPlans(response.data.plans || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load credit plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    form.resetFields();
    form.setFieldsValue({
      currency: 'USD',
      is_active: true,
      is_custom: false,
    });
    setModalVisible(true);
  };

  const handleEdit = (plan: CreditPlan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      plan_code: plan.plan_code,
      plan_name: plan.plan_name,
      credits: plan.default_credits,
      price: plan.price,
      currency: plan.currency,
      description: plan.description,
      is_active: plan.is_active,
      is_custom: plan.is_custom,
    });
    setModalVisible(true);
  };

  const handleDelete = async (planId: string) => {
    try {
      await api.delete(`/api/super-admin/plans/${planId}`);
      message.success('Plan deleted successfully');
      fetchPlans();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete plan');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Transform 'credits' to 'default_credits' for API
      const payload = {
        ...values,
        default_credits: values.credits,
      };
      delete payload.credits;
      
      if (editingPlan) {
        await api.put(`/api/super-admin/plans/${editingPlan.id}`, payload);
        message.success('Plan updated successfully');
      } else {
        await api.post('/api/super-admin/plans', payload);
        message.success('Plan created successfully');
      }
      
      setModalVisible(false);
      fetchPlans();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(error.response?.data?.message || 'Failed to save plan');
    }
  };

  const initializeDefaultPlans = async () => {
    try {
      setLoading(true);
      await api.post('/api/super-admin/plans/initialize');
      message.success('Default plans initialized successfully');
      fetchPlans();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to initialize plans');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Plan Code',
      dataIndex: 'plan_code',
      key: 'plan_code',
      render: (code: string, record: CreditPlan) => (
        <Space>
          <strong>{code}</strong>
          {!record.is_custom && <Tag color="blue">Default</Tag>}
        </Space>
      ),
    },
    {
      title: 'Plan Name',
      dataIndex: 'plan_name',
      key: 'plan_name',
    },
    {
      title: 'Credits',
      dataIndex: 'default_credits',
      key: 'default_credits',
      render: (credits: number) => credits != null ? credits.toLocaleString() : 0,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: CreditPlan) => (
        `${record.currency || 'USD'} ${price != null ? price.toFixed(2) : '0.00'}`
      ),
    },
    {
      title: 'Price per Credit',
      key: 'price_per_credit',
      render: (_: any, record: CreditPlan) => {
        const pricePerCredit = (record.price && record.default_credits) ? record.price / record.default_credits : 0;
        return `$${pricePerCredit.toFixed(4)}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CreditPlan) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          {record.is_custom && (
            <Popconfirm
              title="Delete this plan?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <Title level={2}>Credit Plans Management</Title>
        <Space>
          {plans.length === 0 && (
            <Button
              type="default"
              icon={<CheckCircleOutlined />}
              onClick={initializeDefaultPlans}
            >
              Initialize Default Plans
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Create Plan
          </Button>
        </Space>
      </div>

      <Alert
        message="Credit Plan Management"
        description="Manage credit plans for your tenants. Default plans (BASIC, PROFESSIONAL, ENTERPRISE) cannot be deleted. Custom plans can be created for specific needs."
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <Card>
        <Table
          columns={columns}
          dataSource={plans}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingPlan ? 'Edit Credit Plan' : 'Create Credit Plan'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="plan_code"
            label="Plan Code"
            rules={[{ required: true, message: 'Please enter plan code' }]}
          >
            <Input placeholder="e.g., STARTER" disabled={!!editingPlan && !editingPlan.is_custom} />
          </Form.Item>

          <Form.Item
            name="plan_name"
            label="Plan Name"
            rules={[{ required: true, message: 'Please enter plan name' }]}
          >
            <Input placeholder="e.g., Starter Plan" />
          </Form.Item>

          <Form.Item
            name="credits"
            label="Credits"
            rules={[{ required: true, message: 'Please enter credits' }]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder="e.g., 1000"
            />
          </Form.Item>

          <Form.Item
            name="price"
            label="Price"
            rules={[{ required: true, message: 'Please enter price' }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder="e.g., 10.00"
            />
          </Form.Item>

          <Form.Item
            name="currency"
            label="Currency"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="USD">USD</Option>
              <Option value="EUR">EUR</Option>
              <Option value="GBP">GBP</Option>
              <Option value="INR">INR</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Plan description for customers"
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="is_custom"
            label="Plan Type"
            hidden={!!editingPlan}
          >
            <Select>
              <Option value={false}>Default Plan</Option>
              <Option value={true}>Custom Plan</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CreditPlansPage;
